/**
 * Music: draw the instrument, then play it.
 *
 * Every line you draw becomes a bar that rings, and what it sounds like is
 * decided by the line itself — a long line is low and a short one high,
 * because a string half as long really does sound an octave up. A thick line
 * rings louder and longer than a thin one. Each brush is a different material.
 *
 * Then you drop balls on it. Nothing has to be tuned, because every note comes
 * off the same five-note scale: a child cannot draw a wrong instrument.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { CloudRain, Eraser, Home, Pause, Play, Timer, Trash2 } from 'lucide-preact';
import type { Stroke } from '../engine/stroke.ts';
import { BRUSHES, BRUSH_ORDER, createStroke, extendStroke, finishStroke } from '../engine/stroke.ts';
import type { Vec } from '../engine/vec.ts';
import { createBody, setBehavior } from '../engine/body.ts';
import { World } from '../engine/world.ts';
import { drawSparks, drawStroke, spawnSparks, updateSparks } from '../engine/render.ts';
import type { Character } from '../engine/character.ts';
import { characterFrom, drawFace } from '../engine/character.ts';
import type { Spark } from '../engine/render.ts';
import { createToy } from '../engine/toys.ts';
import { CRAYONS, token } from '../engine/palette.ts';
import type { HeldNote } from '../engine/audio.ts';
import { holdNote, play, playNote } from '../engine/audio.ts';
import type { Voice } from '../lib/instrument.ts';
import { degreeForHeight, heightForLane, LANES, strokeHeightAt, strokeLength, voiceFor, voiceName } from '../lib/instrument.ts';
import { isTap, useSurface } from '../hooks/useSurface.ts';
import { paperPattern } from '../ui/paper.ts';
import { Chip, Coach, ConfirmSheet, CrayonRow, SIZES, SizePicker, ToyButton } from '../ui/controls.tsx';

interface Props {
  onExit: () => void;
}

/** One drawn line, and the voice it sings with. */
interface Bar {
  stroke: Stroke;
  voice: Voice;
  /** Every body this line was built from, so a hit can find its way back. */
  bodyIds: number[];
  /** Counts down after a hit, so the line can flash as it rings. */
  glow: number;
  /** The span the sweeping line passes through. */
  fromX: number;
  toX: number;
}

/** How long the sweep takes to cross the paper once. */
const SWEEP_SECONDS = 4.5;
/** How long a ball lasts before it fades out. */
const BALL_LIFE = 9;

const MAX_BARS = 16;
const MAX_BALLS = 14;
/** Thickness of the invisible bar the ball actually hits. */
const BAR_THICKNESS = 9;
/** A drawn line is simplified to at most this many segments before it is built. */
const MAX_SEGMENTS = 7;

/** Evenly spaced points along the stroke, so a curve stays a curve. */
function segmentsOf(stroke: Stroke): Vec[] {
  const points = stroke.points;
  if (points.length < 2) return points;
  const step = Math.max(1, Math.floor((points.length - 1) / MAX_SEGMENTS));
  const out: Vec[] = [];
  for (let i = 0; i < points.length; i += step) out.push(points[i]);
  const last = points[points.length - 1];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

/**
 * Which register a colour sings in, in scale degrees. Five degrees is an
 * octave, so the six crayons span two octaves from the darkest to the
 * brightest — dark colours low, bright colours high, which is the association
 * everybody already has.
 */
function octaveFor(color: string): number {
  const index = CRAYONS.findIndex((c) => token(c.token) === color);
  if (index < 0) return 0;
  return (index - 2) * 5;
}

/** A thin quad around a segment — the solid thing a ball can bounce off. */
function barQuad(a: Vec, b: Vec, thickness: number): Vec[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * thickness;
  const ny = (dx / len) * thickness;
  return [
    { x: a.x + nx, y: a.y + ny },
    { x: b.x + nx, y: b.y + ny },
    { x: b.x - nx, y: b.y - ny },
    { x: a.x - nx, y: a.y - ny },
  ];
}

export function MusicLab({ onExit }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const worldRef = useRef(new World({ width: 1, height: 1, gravity: 1500 }));
  const barsRef = useRef<Bar[]>([]);
  const activeRef = useRef(new Map<number, Stroke>());
  const sparksRef = useRef<Spark[]>([]);
  const patternRef = useRef<CanvasPattern | null>(null);
  const rainRef = useRef(0);
  const clockRef = useRef(0);
  const sweepRef = useRef(0);
  /** Stage height, so a height on the paper can be turned into a note. */
  const stageRef = useRef(1);
  /** Notes the sweep is holding right now, one per line it is crossing. */
  const heldRef = useRef(new Map<Bar, { note: HeldNote | null; degree: number }>());
  /** How long each ball has been on the paper, so it can fade away. */
  const agesRef = useRef(new Map<number, number>());
  /** One character per ball, so the same ball keeps the same face. */
  const facesRef = useRef(new Map<number, Character>());

  const [color, setColor] = useState(() => token('--crayon-sky'));
  const [brush, setBrush] = useState<Stroke['brush']>('crayon');
  const [size, setSize] = useState<number>(SIZES[1]);
  const [barCount, setBarCount] = useState(0);
  const [raining, setRaining] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [sweeping, setSweeping] = useState(true);
  const [slow, setSlow] = useState(false);
  const [askClear, setAskClear] = useState(false);
  const [lastPlayed, setLastPlayed] = useState<string | null>(null);

  const live = useRef({ color, brush, size, raining, sweeping, erasing });
  live.current = { color, brush, size, raining, sweeping, erasing };

  useEffect(() => {
    worldRef.current.timeScale = slow ? 0.4 : 1;
  }, [slow]);

  /** Nothing may be left sounding when the playhead stops or the room closes. */
  const silence = useCallback(() => {
    for (const entry of heldRef.current.values()) entry.note?.stop();
    heldRef.current.clear();
  }, []);

  useEffect(() => {
    if (!sweeping) silence();
  }, [sweeping, silence]);

  useEffect(() => silence, [silence]);

  /**
   * Sound a bar, and mark it so it flashes while it rings.
   *
   * The colour it was drawn with shifts it by whole octaves, so a red line and
   * a blue line of the same length are the same note in different registers —
   * colour is a property of the sound, not a decoration on top of it.
   */
  const ring = useCallback((bar: Bar, force: number, atY: number) => {
    const voice = voiceFor(bar.stroke, force, atY, stageRef.current);
    playNote(voice.degree + octaveFor(bar.stroke.color), {
      type: voice.type,
      gain: voice.gain,
      duration: voice.duration,
    });
    bar.glow = 1;
  }, []);

  const addBall = useCallback((at: Vec) => {
    const world = worldRef.current;
    const ball = createToy('ball', at, live.current.color);
    setBehavior(ball, 'bouncy');
    world.add(ball);
    while (world.bodies.filter((b) => b.invMass > 0).length > MAX_BALLS) {
      const oldest = world.bodies.find((b) => b.invMass > 0);
      if (oldest) world.remove(oldest.id);
      else break;
    }
  }, []);

  /** Build the physical bar a finished line becomes. */
  const buildBar = useCallback((stroke: Stroke) => {
    const world = worldRef.current;
    const points = segmentsOf(stroke);
    if (points.length < 2 || strokeLength(stroke) < 12) return;

    const bodyIds: number[] = [];
    for (let i = 1; i < points.length; i++) {
      const quad = barQuad(points[i - 1], points[i], BAR_THICKNESS);
      // The bar carries no drawing of its own: the child's actual line is
      // rendered on top, so what they see is what they drew.
      const body = world.add(createBody(quad, [], { static: true }));
      bodyIds.push(body.id);
    }
    if (bodyIds.length === 0) return;

    const xs = stroke.points.map((p) => p.x);
    const bar: Bar = {
      stroke,
      voice: voiceFor(stroke, 0.7, stroke.points[0].y, stageRef.current),
      bodyIds,
      glow: 0,
      fromX: Math.min(...xs),
      toX: Math.max(...xs),
    };
    barsRef.current.push(bar);
    if (barsRef.current.length > MAX_BARS) {
      const gone = barsRef.current.shift();
      for (const id of gone?.bodyIds ?? []) world.remove(id);
    }
    setBarCount(barsRef.current.length);
    setLastPlayed(voiceName(bar.voice.degree));
    ring(bar, 0.5, stroke.points[0].y);
  }, [ring]);

  const barForBody = (id: number): Bar | undefined => barsRef.current.find((b) => b.bodyIds.includes(id));

  /** The line under a finger, if there is one close enough to mean it. */
  const barAt = (x: number, y: number): Bar | undefined =>
    barsRef.current.find((bar) => bar.stroke.points.some((point) => Math.hypot(point.x - x, point.y - y) < 26));

  /**
   * Rub one line out. Its bodies go with it, and so does any note it is in the
   * middle of holding — otherwise the line disappears and carries on singing.
   */
  const rubOut = useCallback((bar: Bar) => {
    const world = worldRef.current;
    heldRef.current.get(bar)?.note?.stop();
    heldRef.current.delete(bar);
    for (const id of bar.bodyIds) world.remove(id);
    barsRef.current = barsRef.current.filter((b) => b !== bar);
    setBarCount(barsRef.current.length);
    play('whoosh', 0.4);
  }, []);

  /** The same ball keeps the same face for as long as it is on the paper. */
  const characterFor = (id: number, color: string): Character => {
    const existing = facesRef.current.get(id);
    if (existing) return existing;
    const made = characterFrom(id * 2654435761, color);
    facesRef.current.set(id, made);
    return made;
  };

  useSurface(canvasRef, {
    onResize: ({ width, height }) => {
      worldRef.current.resize(width, height);
      stageRef.current = height;
    },

    onFrame: (ctx, dt, { width, height }) => {
      const world = worldRef.current;
      world.update(dt);
      updateSparks(sparksRef.current, dt);

      // Every collision asks the bar it hit what it sounds like, and how hard.
      // A ball is the moving body, so the bar is on the other side of the hit.
      for (const impact of world.impacts) {
        const bar = barForBody(impact.otherId) ?? barForBody(impact.bodyId);
        if (!bar) continue;
        ring(bar, Math.min(1, impact.speed / 1200), impact.point.y);
        spawnSparks(sparksRef.current, impact.point, [live.current.color], 5, 200);
      }

      // The sweeping line: it crosses the paper over and over, ringing every
      // line it passes. This is what makes the room play itself — you draw the
      // tune and it goes round for ever, with no moment where it is finished
      // and nothing is happening.
      if (live.current.sweeping && width > 0) {
        const speed = width / SWEEP_SECONDS;
        const from = sweepRef.current;
        let to = from + dt * speed;

        const wrapped = to > width;
        if (wrapped) to -= width;
        sweepRef.current = to;

        // A note sounds for exactly as long as the playhead is crossing the
        // line that makes it: a long line is a long note. While it crosses,
        // the pitch follows the height of the line at that point, so a sloping
        // line walks up or down the scale as the playhead travels along it.
        const held = heldRef.current;
        for (const bar of barsRef.current) {
          const inside = !wrapped && to >= bar.fromX && to <= bar.toX;
          const current = held.get(bar);

          if (!inside) {
            if (current) {
              current.note?.stop();
              held.delete(bar);
            }
            continue;
          }

          const y = strokeHeightAt(bar.stroke, to) ?? bar.stroke.points[0].y;
          const degree = degreeForHeight(y, height) + octaveFor(bar.stroke.color);
          bar.glow = 1;

          if (!current) {
            const voice = voiceFor(bar.stroke, 0.75, y, height);
            held.set(bar, { note: holdNote(degree, { type: voice.type, gain: voice.gain }), degree });
          } else if (degree !== current.degree) {
            current.note?.glideTo(degree);
            current.degree = degree;
          }
        }

        // A line that has just been rubbed out must not keep singing.
        for (const bar of [...held.keys()]) {
          if (!barsRef.current.includes(bar)) {
            held.get(bar)?.note?.stop();
            held.delete(bar);
          }
        }
      }

      if (live.current.raining) {
        rainRef.current -= dt;
        if (rainRef.current <= 0) {
          rainRef.current = 0.75;
          addBall({ x: width * (0.15 + Math.random() * 0.7), y: -20 });
        }
      }

      // Balls do not last for ever: they fade out and go, so the paper clears
      // itself instead of silting up with everything that has ever been played.
      for (const body of [...world.bodies]) {
        if (body.invMass === 0) continue;
        const age = (agesRef.current.get(body.id) ?? 0) + dt;
        agesRef.current.set(body.id, age);
        if (age > BALL_LIFE || body.asleep) {
          world.remove(body.id);
          agesRef.current.delete(body.id);
          facesRef.current.delete(body.id);
        }
      }

      if (!patternRef.current) patternRef.current = paperPattern(ctx);
      ctx.fillStyle = patternRef.current ?? token('--paper');
      ctx.fillRect(0, 0, width, height);

      // The lanes: faint lines showing where each note sits, so the paper
      // reads as a score you can aim at rather than blank space.
      stageRef.current = height;
      ctx.save();
      ctx.strokeStyle = token('--paper-line');
      ctx.lineWidth = 1.5;
      for (let lane = 0; lane < LANES; lane++) {
        const y = heightForLane(lane, height);
        ctx.globalAlpha = lane % 5 === 0 ? 0.9 : 0.4;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      ctx.restore();

      // The sweeping line itself, drawn behind the bars so it passes under them.
      if (live.current.sweeping) {
        ctx.save();
        ctx.strokeStyle = token('--crayon-green');
        ctx.lineWidth = 3;
        ctx.globalAlpha = 0.75;
        ctx.beginPath();
        ctx.moveTo(sweepRef.current, 0);
        ctx.lineTo(sweepRef.current, height);
        ctx.stroke();
        ctx.restore();
      }

      // The bars, drawn as the lines they were drawn as, brightening as they ring.
      for (const bar of barsRef.current) {
        bar.glow = Math.max(0, bar.glow - dt * 2.2);
        ctx.save();
        if (bar.glow > 0.01) {
          ctx.shadowColor = bar.stroke.color;
          ctx.shadowBlur = 4 + bar.glow * 26;
        }
        drawStroke(ctx, bar.stroke);
        ctx.restore();
      }

      // The balls are somebodies. They squash when they land and look startled
      // for a moment, which turns a marble run into a little performance.
      clockRef.current += dt;
      for (const body of world.bodies) {
        if (body.invMass === 0) continue;
        const ch = characterFor(body.id, live.current.color);
        // Fade out over the last couple of seconds, so a ball leaves rather
        // than blinking out of existence.
        const age = agesRef.current.get(body.id) ?? 0;
        const fade = Math.max(0, Math.min(1, (BALL_LIFE - age) / 2));
        // Smooth balls rather than the wobbly character body: in a room that
        // plays itself, clean circles read as motion, and a lumpy outline
        // reads as jitter.
        const r = body.radius * (0.6 + fade * 0.4);
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(body.pos.x, body.pos.y);
        const squash = Math.min(1, body.wobble * 2.4);
        if (squash > 0.001) ctx.scale(1 + squash * 0.22, 1 - squash * 0.2);
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.fillStyle = ch.color;
        ctx.fill();
        ctx.lineWidth = Math.max(2, r * 0.16);
        ctx.strokeStyle = 'rgba(44, 37, 69, 0.85)';
        ctx.stroke();
        drawFace(ctx, ch, { x: 0, y: 0, size: r, t: clockRef.current, mood: body.lastImpact > 0.08 ? 'surprised' : 'happy' });
        ctx.restore();
      }
      for (const s of activeRef.current.values()) drawStroke(ctx, s);
      drawSparks(ctx, sparksRef.current);
    },

    onDown: (p) => {
      if (live.current.erasing) {
        const hit = barAt(p.pos.x, p.pos.y);
        if (hit) rubOut(hit);
        return;
      }
      const { color: c, brush: b, size: s } = live.current;
      const stroke = createStroke(b, c, s, Math.floor(Math.random() * 100000));
      extendStroke(stroke, { ...p.pos });
      activeRef.current.set(p.id, stroke);
    },

    onMove: (p) => {
      // Rubbing out works like a rubber: drag across several and they all go.
      if (live.current.erasing) {
        const hit = barAt(p.pos.x, p.pos.y);
        if (hit) rubOut(hit);
        return;
      }
      const stroke = activeRef.current.get(p.id);
      if (stroke) extendStroke(stroke, { ...p.pos });
    },

    onUp: (p) => {
      if (live.current.erasing) return;
      const stroke = activeRef.current.get(p.id);
      if (!stroke) return;
      activeRef.current.delete(p.id);

      // A tap drops a ball; a drag draws a bar. One finger does both, which is
      // what keeps the room to a single gesture a five-year-old already knows.
      if (isTap(p)) {
        const hit = barsRef.current.find((bar) =>
          bar.stroke.points.some((point) => Math.hypot(point.x - p.pos.x, point.y - p.pos.y) < 26)
        );
        if (hit) {
          ring(hit, 0.85, p.pos.y);
          setLastPlayed(voiceName(degreeForHeight(p.pos.y, stageRef.current)));
        } else {
          addBall({ ...p.pos });
          play('pop', 0.5);
        }
        return;
      }

      buildBar(finishStroke(stroke));
    },
  });

  const clear = () => {
    silence();
    worldRef.current.clear();
    barsRef.current = [];
    activeRef.current.clear();
    setBarCount(0);
    setRaining(false);
    setAskClear(false);
    setLastPlayed(null);
    play('whoosh', 0.6);
  };

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Draw an instrument" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton
              round
              label={sweeping ? 'Stop the line' : 'Let it play itself'}
              hideLabel
              pressed={sweeping}
              onClick={() => setSweeping((v) => !v)}
            >
              {sweeping ? <Pause size={22} strokeWidth={2.5} /> : <Play size={24} strokeWidth={2.5} />}
            </ToyButton>
            <ToyButton round label={slow ? 'Normal speed' : 'Slow it down'} hideLabel pressed={slow} onClick={() => setSlow((v) => !v)}>
              <Timer size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Start again" hideLabel disabled={barCount === 0} onClick={() => setAskClear(true)}>
              <Trash2 size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>
            {barCount} line{barCount === 1 ? '' : 's'}
          </strong>
          <span>{lastPlayed ? `that one is ${lastPlayed}` : 'long lines are low, short ones are high'}</span>
        </p>

        <Coach>{barCount === 0 ? 'Draw a line, then tap the paper to drop a ball' : 'Tap a line to play it'}</Coach>
      </div>

      <div class="dock">
        <CrayonRow value={color} onChange={setColor} />
        <div class="dock__row">
          <div class="dock__row dock__row--scroll">
            {BRUSH_ORDER.map((id) => (
              <Chip
                key={id}
                label={BRUSHES[id].label}
                pressed={brush === id && !erasing}
                onClick={() => {
                  setBrush(id);
                  setErasing(false);
                }}
              />
            ))}
            <Chip label="Rub out" pressed={erasing} onClick={() => setErasing((v) => !v)}>
              <Eraser size={20} strokeWidth={2.75} />
            </Chip>
            <Chip label="Rain" pressed={raining} onClick={() => setRaining((v) => !v)}>
              <CloudRain size={20} strokeWidth={2.75} />
            </Chip>
          </div>
          <SizePicker value={size} onChange={setSize} />
        </div>
      </div>

      {askClear && (
        <ConfirmSheet title="Clear the instrument?" confirmLabel="Clear it" onConfirm={clear} onCancel={() => setAskClear(false)} />
      )}
    </div>
  );
}
